/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shopService, Shop } from '../services/shopService';
import { apiService } from '../services/api';
import { getValidImageUrl } from '../utils/imageUtils';
import { useShopTypeTranslation } from '../utils/categoryTranslations';
import { useTranslations } from '../hooks/useTranslations';
import CustomerHeader from '../components/CustomerHeader';
import { useCartCount } from '../hooks/useCartCount';
import './ShopDetailsPage.css';

const ShopDetailsPage: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const translateShopType = useShopTypeTranslation();
  const t = useTranslations();
  const [shop, setShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Use shared hook for cart count
  const cartCount = useCartCount(500);

  useEffect(() => {
    if (shopId) {
      loadShopDetails();
    }
  }, [shopId]);

  const loadShopDetails = async () => {
    if (!shopId) return;
    
    try {
      setLoading(true);
      // Try API first
      try {
        const shops = await apiService.getShops();
        const foundShop = shops.find(s => s.id === shopId);
        if (foundShop) {
          const normalizedShop: Shop = {
            ...foundShop,
            logo: getValidImageUrl(foundShop.logo || foundShop.logo_url || foundShop.image || foundShop.image_url || foundShop.photo || ''),
          };
          setShop(normalizedShop);
          console.log('✅ Shop details loaded:', normalizedShop.name);
          return;
        }
      } catch (apiError) {
        console.warn('API shops not available, using shopService:', apiError);
      }
      
      // Fallback to shopService
      const shopData = await shopService.getShop(shopId);
      setShop(shopData);
    } catch (error) {
      console.error('Error loading shop details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getShopTypeIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      grocery: '🛒',
      pharmacy: '💊',
      bakery: '🥖',
      electronics: '📱',
      clothing: '👕',
      medical: '🏥',
      restaurant: '🍽️',
      specialty: '⭐',
      general: '🏪'
    };
    return icons[type] || '🏪';
  };

  const getShopTypeDisplayName = (type: string): string => {
    return translateShopType(type);
  };

  const formatAddress = () => {
    if (!shop) return '';
    return `${shop.address}, ${shop.city}, ${shop.state} ${shop.country}`;
  };

  if (loading) {
    return (
      <div className="shop-details-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t.loadingShopDetails || 'Loading shop details...'}</p>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="shop-details-page">
        <div className="error-container">
          <h2>{t.shopNotFound || 'Shop Not Found'}</h2>
          <p>{t.shopNotFoundMessage || 'The shop you\'re looking for doesn\'t exist or has been removed.'}</p>
          <button onClick={() => navigate('/')} className="back-btn">
            {t.goBackHome || 'Go Back Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-details-page">
      {/* Header - Using CustomerHeader component for consistency */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton={true}
        showLoyaltyButton
        cartCount={cartCount}
        onBackClick={() => navigate('/app')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/app', { state: { showCart: true } })}
      />

      {/* Shop Details Content */}
      <div className="shop-details-content">
        <div className="shop-details-card">
          {/* Shop Header */}
          <div className="shop-header-section">
            <div className="shop-logo-container">
              {shop.logo ? (
                <img 
                  src={shop.logo} 
                  alt={shop.name}
                  className="shop-logo-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent && !parent.querySelector('.shop-icon-large')) {
                      const fallback = document.createElement('div');
                      fallback.className = 'shop-icon-large';
                      fallback.textContent = getShopTypeIcon(shop.type);
                      parent.appendChild(fallback);
                    }
                  }}
                />
              ) : (
                <div className="shop-icon-large">{getShopTypeIcon(shop.type)}</div>
              )}
            </div>
            <div className="shop-title-section">
              <h1 className="shop-name">{shop.name}</h1>
              <div className="shop-type-badge">{getShopTypeDisplayName(shop.type)}</div>
              <div className="shop-status-badge">
                {shop.is_active ? (
                  <span className="status-active">🟢 {t.open || 'Open'}</span>
                ) : (
                  <span className="status-inactive">🔴 {t.closed || 'Closed'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Shop Information Sections */}
          <div className="shop-info-sections">
            {shop.description && (
              <div className="info-section">
                <h3>📝 {t.description || 'Description'}</h3>
                <p className="info-text">{shop.description}</p>
              </div>
            )}

            <div className="info-section">
              <h3>📍 {t.location || 'Location'}</h3>
              <p className="info-text">{formatAddress()}</p>
            </div>

            {(shop.phone || shop.email) && (
              <div className="info-section">
                <h3>📞 {t.contactInformation || 'Contact Information'}</h3>
                <div className="contact-info">
                  {shop.phone && (
                    <div className="contact-item">
                      <span className="contact-label">{t.phone || 'Phone'}:</span>
                      <span className="contact-value">{shop.phone}</span>
                    </div>
                  )}
                  {shop.email && (
                    <div className="contact-item">
                      <span className="contact-label">{t.email || 'Email'}:</span>
                      <span className="contact-value">{shop.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="info-section">
              <h3>🏪 {t.shopStatus || 'Shop Status'}</h3>
              <div className="status-info">
                <div className={`status-badge-large ${shop.is_active ? 'active' : 'inactive'}`}>
                  {shop.is_active ? `🟢 ${t.open || 'Open'}` : `🔴 ${t.closed || 'Closed'}`}
                </div>
                <p className="status-text">
                  {shop.is_active 
                    ? (t.shopCurrentlyOpen || 'This shop is currently open and accepting orders')
                    : (t.shopCurrentlyClosed || 'This shop is currently closed or unavailable')
                  }
                </p>
              </div>
            </div>

            {shop.rating && (
              <div className="info-section">
                <h3>⭐ {t.rating || 'Rating'}</h3>
                <div className="rating-info">
                  <span className="rating-value">{shop.rating.toFixed(1)}</span>
                  <span className="rating-stars">⭐⭐⭐⭐⭐</span>
                  {shop.total_reviews && (
                    <span className="rating-reviews">({shop.total_reviews} {t.reviews || 'reviews'})</span>
                  )}
                </div>
              </div>
            )}

            {shop.theme && (
              <div className="info-section">
                <h3>🎨 {t.shopTheme || 'Shop Theme'}</h3>
                <div className="theme-preview">
                  <div 
                    className="theme-color" 
                    style={{ backgroundColor: shop.theme.primary_color }}
                    title="Primary Color"
                  ></div>
                  <div 
                    className="theme-color" 
                    style={{ backgroundColor: shop.theme.secondary_color }}
                    title="Secondary Color"
                  ></div>
                  <div 
                    className="theme-color" 
                    style={{ backgroundColor: shop.theme.accent_color }}
                    title="Accent Color"
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="shop-details-actions">
            {/* Primary Action: Browse Products - Clear call to action */}
            <button 
              className="browse-products-btn primary-action" 
              onClick={() => navigate(`/shop/${shop.id}`)}
              disabled={!shop.is_active}
              title="View and shop products from this store"
            >
              🛒 {shop.is_active ? (t.browseProducts || 'Browse Products') : (t.shopUnavailable || 'Shop Unavailable')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopDetailsPage;

